import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

// UI Components
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/common-utils/components/ui/accordion';
import { Button } from '@/common-utils/components/ui/button';
import { Separator } from '@/common-utils/components/ui/separator';

// Renders/Other Components
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import Icon from '@/common-utils/components/renders/icon/Icon';
import BeneficiarySummary from '@/app/modules/payludd/beneficiaries/components/BeneficiarySummary';
import ContractCard from '@/app/modules/payludd/beneficiaries/components/ContractCard';

// Enums & Fields
import ICONS from '@/common-utils/enums/icons';
import BENEFICIARY_COLORS from '@/app/modules/payludd/beneficiaries/enums/BeneficiaryColors';
import customizationFields from '@/app/modules/payludd/customization/fields/customizationFields';
import contractFields from '@/app/modules/contracts/fields/contractFields';
import BENEFICIARY_ACTION_TYPES from '../enums/BeneficiaryActionTypes';

// Lang/Namespaces
import { nsBeneficiaries } from '@/app/i18n/namespaces';

/**
 *
 * @param root0
 * @param root0.beneficiaries
 * @param root0.openItems
 * @param root0.setOpenItems
 * @param root0.colorPickerBeneficiaryUuid
 * @param root0.setColorPickerBeneficiaryUuid
 * @param root0.setActiveModal
 * @param root0.onUpdateBeneficiary
 * @param root0.showActions
 * @param root0.displayVariant
 * @returns {JSX} BeneficiaryAccordion
 */
function BeneficiaryAccordion({
    beneficiaries,
    openItems,
    setOpenItems,
    colorPickerBeneficiaryUuid,
    setColorPickerBeneficiaryUuid,
    setActiveModal,
    onUpdateBeneficiary,
    showActions = true,
    displayVariant = 'default'
}) {
    const isTpeVariant = displayVariant === 'tpe';

    useEffect(() => {
        if (showActions && beneficiaries?.length) {
            setOpenItems(beneficiaries.map((_, index) => `beneficiary-${index}`));
        }
    }, [beneficiaries, showActions, setOpenItems]);

    const toggleItem = (itemValue) => {
        setOpenItems((prev) =>
            prev.includes(itemValue)
                ? prev.filter((value) => value !== itemValue)
                : [...prev, itemValue]
        );
    };

    const renderBeneficiaryContent = ({ id, name, color, contracts }) => (
        <>
            <PageActions
                className={`mb-2 ${!showActions && 'hidden'}`}
                actions={[
                    {
                        icon: ICONS.LINK,
                        entity: 'actions.beneficiary.associate_contract',
                        namespace: nsBeneficiaries,
                        onClick: () => setActiveModal({
                            type: BENEFICIARY_ACTION_TYPES.ASSOCIATE_CONTRACT,
                            beneficiary: {
                                id,
                                name,
                                contractsIds: contracts.map(contract => contract[contractFields.UUID.key])
                            }
                        }),
                        visible: true,
                        variant: 'primary'
                    },
                    {
                        visible: true,
                        render: () => (
                            <Button
                                variant='secondary'
                                onClick={() => {
                                    setColorPickerBeneficiaryUuid(prev =>
                                        prev === id ? null : id
                                    );
                                }}
                            >
                                <span
                                    className='w-4 h-4 rounded-full block shrink-0 border border-dark-foreground'
                                    style={{ backgroundColor: color }}
                                />
                            </Button>
                        )
                    },
                    {
                        icon: ICONS.EDIT,
                        entity: 'actions.beneficiary.update_label',
                        namespace: nsBeneficiaries,
                        onClick: () => setActiveModal({
                            type: BENEFICIARY_ACTION_TYPES.UPDATE_LABEL,
                            beneficiary: {
                                id,
                                name
                            }
                        }),
                        visible: true,
                        variant: 'secondary'
                    },
                    {
                        icon: ICONS.DELETE,
                        entity: 'actions.beneficiary.delete',
                        namespace: nsBeneficiaries,
                        onClick: () => setActiveModal({
                            type: BENEFICIARY_ACTION_TYPES.DELETE_BENEFICIARY,
                            beneficiary: {
                                id,
                                name,
                                color,
                                contracts
                            }
                        }),
                        visible: true,
                        variant: 'critical'
                    }
                ]}
                retractable
            />
            {colorPickerBeneficiaryUuid === id && (
                <div className='flex items-center gap-2 mb-4 p-2 border rounded-md w-fit bg-white shadow-sm'>
                    {BENEFICIARY_COLORS.map((colorOption) => (
                        <Button
                            key={colorOption}
                            className='w-6 h-6 p-0 rounded-full border-2 cursor-pointer hover:scale-110 transition-transform shrink-0'
                            style={{
                                backgroundColor: colorOption,
                                borderColor: colorOption === color ? '#18181B' : 'transparent'
                            }}
                            onClick={() =>
                                onUpdateBeneficiary({
                                    beneficiaryUuid: id,
                                    payload: { color: colorOption },
                                    successEntity: 'actions.beneficiary.update_color.success',
                                    errorEntity: 'actions.beneficiary.update_color.error'
                                })
                            }
                            title={colorOption}
                        />
                    ))}
                </div>
            )}
            <div className={`flex flex-col gap-3 sm:flex-row sm:flex-wrap ${isTpeVariant ? 'mt-3' : 'mt-4'}`}>
                {contracts.map((contract) => (
                    <ContractCard
                        key={contract[contractFields.UUID.key]}
                        contract={contract}
                        submitDissociateContract={() =>
                            onUpdateBeneficiary({
                                beneficiaryUuid: id,
                                payload: {
                                    contractsProfileUuids: contracts
                                        .map(item => item[contractFields.UUID.key])
                                        .filter(uuid => uuid !== contract[contractFields.UUID.key])
                                },
                                successEntity: 'actions.beneficiary.dissaociate_contract.success',
                                errorEntity: 'actions.beneficiary.dissaociate_contract.error'
                            })
                        }
                        showActions={showActions}
                        displayVariant={displayVariant}
                    />
                ))}
            </div>
        </>
    );

    if (isTpeVariant) {
        return (
            <div>
                {beneficiaries.map((beneficiary, index) => {
                    const itemValue = `beneficiary-${index}`;
                    const contracts = beneficiary[customizationFields.BENEFICIARIES.CONTRACTS.key] ?? [];
                    const color = beneficiary[customizationFields.BENEFICIARIES.COLOR.key];
                    const id = beneficiary[customizationFields.BENEFICIARIES.UUID.key];
                    const name = beneficiary[customizationFields.BENEFICIARIES.NAME.key];
                    const isOpen = openItems.includes(itemValue);

                    return (
                        <div key={id} className='mb-3'>
                            <button
                                type='button'
                                className='flex w-full items-center justify-between px-0 py-2 font-medium outline-none'
                                onClick={() => toggleItem(itemValue)}
                            >
                                <BeneficiarySummary
                                    color={color}
                                    name={name}
                                    contracts={contracts}
                                    displayVariant={displayVariant}
                                />
                                <Icon
                                    icon={ICONS.CHEVRON_DOWN}
                                    className={`shrink-0 transition-transform duration-200 ${isOpen && 'rotate-180'}`}
                                />
                            </button>
                            <Separator className='my-2' />
                            {isOpen && renderBeneficiaryContent({ id, name, color, contracts })}
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <Accordion type='multiple' value={openItems} onValueChange={setOpenItems}>
            {beneficiaries.map((beneficiary, index) => {
                const itemValue = `beneficiary-${index}`;
                const contracts = beneficiary[customizationFields.BENEFICIARIES.CONTRACTS.key] ?? [];
                const color = beneficiary[customizationFields.BENEFICIARIES.COLOR.key];
                const id = beneficiary[customizationFields.BENEFICIARIES.UUID.key];
                const name = beneficiary[customizationFields.BENEFICIARIES.NAME.key];
                const isOpen = openItems.includes(itemValue);

                return (
                    <AccordionItem className='rounded-md' value={itemValue} key={id}>
                        <AccordionTrigger open={isOpen}>
                            <BeneficiarySummary
                                color={color}
                                name={name}
                                contracts={contracts}
                                displayVariant={displayVariant}
                            />
                        </AccordionTrigger>
                        <AccordionContent>
                            {renderBeneficiaryContent({ id, name, color, contracts })}
                        </AccordionContent>
                    </AccordionItem>
                );
            })}
        </Accordion>
    );
}

BeneficiaryAccordion.propTypes = {
    beneficiaries: PropTypes.array.isRequired,
    openItems: PropTypes.array.isRequired,
    setOpenItems: PropTypes.func.isRequired,
    colorPickerBeneficiaryUuid: PropTypes.string,
    setColorPickerBeneficiaryUuid: PropTypes.func,
    setActiveModal: PropTypes.func,
    onUpdateBeneficiary: PropTypes.func,
    showActions: PropTypes.bool,
    displayVariant: PropTypes.oneOf(['default', 'tpe'])
};

export default BeneficiaryAccordion;
